import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCalendar, FiMapPin, FiUsers, FiHeart, FiExternalLink } from 'react-icons/fi';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Project interface
interface Project {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
  date: string;
  location: string;
  impact: string;
  link?: string;
}

const Projects: React.FC = () => {
  // Categorieën voor filtering
  const categories = ["Alle projecten", "Lokaal", "Nationaal", "Internationaal", "Jeugd", "Gezondheid"];
  const [activeCategory, setActiveCategory] = useState("Alle projecten");

  // Projecten data
  const projects: Project[] = [
    {
      id: 1,
      title: "Hospice Dignitas Hoorn",
      description: "Ondersteuning van Hospice Dignitas in Hoorn, waar terminaal zieke mensen in een huiselijke omgeving hun laatste levensfase kunnen doorbrengen. Lions Club Hoorn De Doelen heeft bijgedragen aan de aanschaf van speciale bedden en andere benodigdheden.",
      image: "./images/project-hospice.jpg",
      category: "Lokaal",
      date: "Doorlopend project",
      location: "Hoorn",
      impact: "Verbeterde zorg voor terminaal zieke patiënten",
      link: "https://www.hospicehoorn.nl"
    },
    {
      id: 2,
      title: "Jaarlijkse Boekenmarkt",
      description: "Elk jaar organiseert Lions Club Hoorn De Doelen een grote boekenmarkt in het centrum van Hoorn. De opbrengst gaat naar verschillende lokale goede doelen. In 2023 hebben we meer dan €5.000 opgehaald.",
      image: "./images/project-boekenmarkt.jpg",
      category: "Lokaal",
      date: "April 2023",
      location: "Centrum Hoorn",
      impact: "€5.000 opgehaald voor lokale goede doelen"
    },
    {
      id: 3,
      title: "Warmte voor Oekraïne",
      description: "In samenwerking met andere Lions Clubs in Nederland hebben we geld ingezameld voor de aanschaf van generatoren en verwarmingsapparatuur voor Oekraïne tijdens de wintermaanden.",
      image: "./images/project-ukraine.jpg",
      category: "Internationaal",
      date: "Winter 2022-2023",
      location: "Oekraïne",
      impact: "Bijgedragen aan de aanschaf van 50+ generatoren"
    },
    {
      id: 4,
      title: "Vredesposterwedstrijd",
      description: "Jaarlijkse internationale wedstrijd waarbij kinderen van 11-13 jaar worden uitgenodigd om hun visie op vrede te delen via kunst. Lions Club Hoorn De Doelen organiseert deze wedstrijd voor scholen in de regio Hoorn.",
      image: "./images/project-peace-poster.jpg",
      category: "Jeugd",
      date: "September-November 2023",
      location: "Scholen in regio Hoorn",
      impact: "Meer dan 200 deelnemende kinderen"
    },
    {
      id: 5,
      title: "Voedselbank Westfriesland",
      description: "Regelmatige ondersteuning van de Voedselbank Westfriesland door middel van inzamelingsacties en vrijwilligerswerk. Lions Club leden helpen bij het sorteren en distribueren van voedselpakketten.",
      image: "./images/project-voedselbank.jpg",
      category: "Lokaal",
      date: "Doorlopend project",
      location: "Westfriesland",
      impact: "Ondersteuning van 150+ gezinnen per week",
      link: "https://www.voedselbankwf.nl"
    },
    {
      id: 6,
      title: "Sight First",
      description: "Deelname aan het internationale Lions Sight First programma, dat zich richt op het voorkomen van vermijdbare blindheid wereldwijd. Lions Club Hoorn De Doelen zamelt brillen in en ondersteunt oogzorgprojecten.",
      image: "./images/project-sight-first.jpg",
      category: "Internationaal",
      date: "Doorlopend project",
      location: "Wereldwijd",
      impact: "Bijgedragen aan oogzorg voor duizenden mensen",
      link: "https://www.lionsclubs.org/en/discover-our-foundation/mission"
    },
    {
      id: 7,
      title: "Beursvloer Westfriesland",
      description: "Ondersteuning van de Beursvloer Westfriesland, een evenement waar bedrijven en maatschappelijke organisaties elkaar ontmoeten en matches maken op basis van vraag en aanbod, zonder geld.",
      image: "./images/project-beursvloer.jpg",
      category: "Lokaal",
      date: "November 2023",
      location: "Hoorn",
      impact: "Meer dan 100 matches tussen bedrijven en goede doelen"
    },
    {
      id: 8,
      title: "Diabetes Bewustwording",
      description: "Campagne voor bewustwording rond diabetes, een van de vijf focusgebieden van Lions Clubs International. Lions Club Hoorn De Doelen organiseert informatiesessies en ondersteunt lokale initiatieven.",
      image: "./images/project-diabetes.jpg",
      category: "Gezondheid",
      date: "Doorlopend project",
      location: "Hoorn en omgeving",
      impact: "Verhoogde bewustwording en vroegtijdige detectie"
    },
    {
      id: 9,
      title: "Jeugdfonds Sport & Cultuur",
      description: "Ondersteuning van het Jeugdfonds Sport & Cultuur, dat kinderen uit gezinnen met beperkte financiële middelen de kans geeft om te sporten of culturele activiteiten te ondernemen.",
      image: "./images/project-jeugdfonds.jpg",
      category: "Jeugd",
      date: "Doorlopend project",
      location: "Hoorn en omgeving",
      impact: "Jaarlijks 20+ kinderen geholpen",
      link: "https://jeugdfondssportencultuur.nl/"
    }
  ];

  // Filter projecten op basis van geselecteerde categorie
  const filteredProjects = activeCategory === "Alle projecten" 
    ? projects 
    : projects.filter(project => project.category === activeCategory);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-lions-dark text-white py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="./images/projects-hero.jpg" 
            alt="Projecten van Lions Club Hoorn De Doelen" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.h1 
              variants={fadeIn}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Onze <span className="text-lions-yellow">Projecten</span>
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Ontdek de verschillende projecten en initiatieven die Lions Club Hoorn De Doelen ondersteunt, zowel lokaal als internationaal.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-lions-light dark:bg-gray-900 sticky top-0 z-20 shadow-md">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="flex flex-wrap justify-center gap-4"
          >
            {categories.map((category, index) => (
              <motion.button
                key={index}
                variants={fadeIn}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm md:text-base transition-all ${
                  activeCategory === category 
                    ? 'bg-lions-blue dark:bg-lions-yellow text-white dark:text-lions-dark font-bold' 
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {category}
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-white dark:bg-lions-dark">
        <div className="container-custom">
          {filteredProjects.length === 0 ? (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="text-center py-12"
            >
              <h3 className="text-2xl font-bold text-lions-blue dark:text-lions-yellow mb-4">
                Geen projecten gevonden
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                Er zijn momenteel geen projecten in deze categorie. Bekijk andere categorieën of kom later terug.
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  variants={fadeIn}
                  className="bg-lions-light dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg flex flex-col h-full"
                >
                  <div className="relative h-60">
                    <img 
                      src={project.image} 
                      alt={project.title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4 bg-lions-blue dark:bg-lions-yellow text-white dark:text-lions-dark text-sm font-bold py-1 px-3 rounded-full">
                      {project.category}
                    </div>
                  </div>
                  <div className="p-6 flex-grow">
                    <h3 className="text-xl font-bold mb-3 text-lions-blue dark:text-lions-yellow">
                      {project.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      {project.description}
                    </p>
                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center">
                        <FiCalendar className="mr-2" />
                        <span>{project.date}</span>
                      </div>
                      <div className="flex items-center">
                        <FiMapPin className="mr-2" />
                        <span>{project.location}</span>
                      </div>
                      <div className="flex items-center">
                        <FiHeart className="mr-2" />
                        <span>{project.impact}</span>
                      </div>
                    </div>
                  </div>
                  {project.link && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      <a 
                        href={project.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-lions-blue dark:text-lions-yellow hover:underline flex items-center"
                      >
                        Meer informatie <FiExternalLink className="ml-1" />
                      </a>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-lions-blue dark:bg-gray-900 text-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl font-bold mb-4 text-white dark:text-lions-yellow"
            >
              Onze Impact
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-lg max-w-3xl mx-auto"
            >
              Als Lions Club Hoorn De Doelen zijn we trots op wat we samen hebben bereikt.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-4 gap-8 text-center"
          >
            <motion.div variants={fadeIn}>
              <div className="text-4xl font-bold text-lions-yellow dark:text-white mb-2">€25.000+</div>
              <p>Opgehaald in 2023</p>
            </motion.div>
            <motion.div variants={fadeIn}>
              <div className="text-4xl font-bold text-lions-yellow dark:text-white mb-2">9</div>
              <p>Actieve projecten</p>
            </motion.div>
            <motion.div variants={fadeIn}>
              <div className="text-4xl font-bold text-lions-yellow dark:text-white mb-2">500+</div>
              <p>Mensen geholpen</p>
            </motion.div>
            <motion.div variants={fadeIn}>
              <div className="text-4xl font-bold text-lions-yellow dark:text-white mb-2">18</div>
              <p>Jaar actief in Hoorn</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Get Involved Section */}
      <section className="py-16 bg-white dark:bg-lions-dark">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeIn}>
              <h2 className="text-3xl font-bold mb-6 text-lions-blue dark:text-lions-yellow">
                Doe mee en maak verschil
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Er zijn verschillende manieren waarop u kunt bijdragen aan onze projecten en initiatieven:
              </p>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start">
                  <FiUsers className="text-lions-blue dark:text-lions-yellow mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-gray-100">Word lid</h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      Sluit u aan bij Lions Club Hoorn De Doelen en help actief mee met onze projecten.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <FiHeart className="text-lions-blue dark:text-lions-yellow mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-gray-100">Doneer</h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      Ondersteun onze projecten met een financiële bijdrage. 100% van uw donatie gaat naar goede doelen.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <FiCalendar className="text-lions-blue dark:text-lions-yellow mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-gray-100">Bezoek onze evenementen</h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      Kom naar onze fundraising evenementen zoals de jaarlijkse boekenmarkt of wijnproeverij.
                    </p>
                  </div>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4">
                <Link to="/lid-worden" className="btn-primary">
                  Word lid
                </Link>
                <Link to="/contact" className="btn-secondary">
                  Neem contact op
                </Link>
              </div>
            </motion.div>
            <motion.div variants={fadeIn} className="relative">
              <img 
                src="./images/get-involved.jpg" 
                alt="Doe mee met Lions Club Hoorn De Doelen" 
                className="rounded-lg shadow-lg w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-lions-yellow dark:bg-lions-blue text-lions-dark dark:text-white p-4 rounded-lg shadow-lg">
                <p className="font-bold text-xl">Samen</p>
                <p>Maken we verschil</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Projects; 